import { type ApolloError, gql, useQuery } from '@apollo/react-hooks';

import {
  modeOfStudyOptions,
  programmeTypeOptions,
} from '../../Application/components/ApplicationForm/ProgrammeChoice/options';
import { PAGE_TEXT } from '../../Nomination/components/lang';
import { type ScholarshipListData } from '../types';
import { MONITOR_LIST_TEXT } from '../../Assessment/lang';
import { type Paginator } from '../../Nomination/graphql/NominationForm';
import { ASSESSMENT_STATUS_DISPLAY_MAP } from '../../../utils';

type GetScholarshipListPageData = {
  scholarshipListPageData: ScholarshipListPageData;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

type ScholarshipListPageData = {
  data: ScholarshipListData[];
  totalPage: number;
  count: number;
};
const PROGRAMME_LEVEL = MONITOR_LIST_TEXT.PROGRAMME_LEVEL;
const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

const GET_SCHOLARSHIP_LIST = gql`
  query (
    $filter: NominationFilter
    $sort: NominationSort
    $paginator: PaginatorOptions
  ) {
    nominationsPaginated(filter: $filter, sort: $sort, paginator: $paginator) {
      data {
        application {
          _id
          applicationNo
          applicant {
            name {
              givenName
              surname
            }
          }
          programmeChoice {
            department
            programmeType
            programme
            researchArea
            studyMode
            entryYear
          }
          status
        }
        fundings {
          source
        }
      }
      paginator {
        total_pages
        count
      }
    }
  }
`;

export const useGetScholarshipListPageData = (
  filter: any,
  sort: any,
  paginator: Paginator,
): GetScholarshipListPageData => {
  const { data, loading, error, refetch } = useQuery(GET_SCHOLARSHIP_LIST, {
    variables: { filter, sort, paginator },
    fetchPolicy: 'network-only',
  });

  let scholarshipListPageData: ScholarshipListPageData = {
    data: [],
    totalPage: 0,
    count: 0,
  };

  if (!loading && !error) {
    const applicationsContent = data?.nominationsPaginated?.data ?? [];

    const applications: ScholarshipListData[] = applicationsContent.map(
      (element: any) => {
        const { application, fundings = [] } = element;

        const studymode = modeOfStudyOptions.find(
          option => option.value === application.programmeChoice?.studyMode,
        );
        const studyModeLabel = studymode?.label?.toString();
        const programmetype = programmeTypeOptions.find(
          option => option.value === application.programmeChoice?.programmeType,
        );
        const programmeTypeLabel = programmetype?.label?.toString();

        return {
          applicationId: application._id,
          applicationNo: application.applicationNo,
          applicantName: `${application.applicant?.name?.surname?.toUpperCase()} ${application.applicant?.name.givenName}`,
          department: application.programmeChoice?.department,
          studyMode: studyModeLabel,

          progLevel:
            PROGRAMME_LEVEL[
              application.programmeChoice
                ?.programme as keyof typeof PROGRAMME_LEVEL
            ],
          progType: programmeTypeLabel,
          entryYear: application.programmeChoice?.entryYear,
          researchArea: application.programmeChoice?.researchArea,
          fundingScheme:
            (fundings ?? [])?.length === 0
              ? ['N/A']
              : fundings?.map(
                  (funding: any) =>
                    fundingSourceValues[
                      funding?.source as keyof typeof fundingSourceValues
                    ],
                ),
          rawStatus: application?.status,
          status:
            ASSESSMENT_STATUS_DISPLAY_MAP[
              application?.status as keyof typeof ASSESSMENT_STATUS_DISPLAY_MAP
            ],
        };
      },
    );

    const totalPage = data?.nominationsPaginated?.paginator?.total_pages ?? 1;
    const count = data?.nominationsPaginated?.paginator?.count ?? 0;

    scholarshipListPageData = { data: applications, totalPage, count };
  }

  return { scholarshipListPageData, loading, error, refetch };
};
